import StateMachine from 'javascript-state-machine'
import { generateGUID, asyncWrapper, tracelog } from "../../common/common"
import { MediaType } from "../../common/enums"
import protocolSender from "../util/protocol-sender"
import logger from "../../common/logger"
import MsgDispatcher from '../util/msg-dispatcher'
import HstWebSocket from "../util/websocket"
import eventRegistry from '../../common/event-registry'
// import mediaManager from "../media/media-manager"
import MediaStats from "./media-stats"

/**
 * 下行媒体通道（对应WebRTC中的一个PeerConnection）
 */
class DownPeerConnection extends MediaStats {
  constructor(bindId, mediaManager) {
    super()

    // 初始化状态机
    this._fsm()

    // WebRTC的PeerConnection
    this.pc = null
    
    // 关联的video标签（当前仅供外部使用）
    this.videoEle = null

    // 由信令服务器分配，在信令上唯一标识一个PeerConnection
    this.bindId = bindId
    
    // 由媒体服务器分配，在媒体上唯一标识一个PeerConnection
    this.pcId = null

    // 需要和对端约束所有Track都加入同一个MediaStream
    this.mediaStream = null

    // 手动生成一个GUID作为Stream标识，自动恢复使用
    this.mediaStreamId = generateGUID()

    this.mediaManager = mediaManager

    // PeerConnection中所包含的媒体信息，原则上同一个
    // PeerConnection中可以包含多路任意用户的任意媒体
    // DownTrack 
    // {
    //   userId: xxx, 
    //   mediaType: xxx, 
    //   mediaId: xxx,
    //   streamToken: xxx,
    //   streamId: xxx // 平台流唯一标识
    // }
    this.downTracks = new Array()
    
    // 每个PeerConnection绑定一个WebSocket
    this.websocket = null

    // 媒体服务器websocket地址
    this.streamServer = null

    // 请求接收队列
    this.startRecvQueue = new Array()

    // 停止接收队列
    this.stopRecvQueue = new Array()

    this.videoBillStarted = false

    // 协议处理
    this.msgDispatcher = new MsgDispatcher()
    this.msgDispatcher.addMsgHandler(19005, this._processLoginResp.bind(this))
    this.msgDispatcher.addMsgHandler(19009, this._processSdpNegotiation.bind(this))
    this.msgDispatcher.addMsgHandler(19013, this._processLogoutResp.bind(this))
    this.msgDispatcher.addMsgHandler(19015, this._processResolutionNotify.bind(this))
    this.msgDispatcher.addMsgHandler(19017, this._processResolutionChange.bind(this))

    return this
  }
  /**
   * 上报收流的窗口的宽高，匹配合适的分辨率 19016协议上报
   * @param {*} params 
   */
  matchResolution(params) {
    // 上报收流窗口的宽高
    protocolSender.sendResolutionChangeReq(this.websocket, params)
  }
  /**
   * 通知分辨率的改变的回应 19017协议
   */ 

  _processResolutionChange(msg) {
    // 适配svc时返回的确认消息,svc切换成功时为0
    if(msg.result != 0) logger.warn('svc is invalid !')
  }

  /**
   * 获取视频统计数据
   */
  getVideoStats() {
    return this.pc ? this.stats.video : null
  }

  /**
   * 获取音频统计数据
   */
  getAudioStats() {
    return this.pc ? this.stats.audio : null
  }

  /**
   * 开始接收媒体
   * @param {*} params 
   */
  @tracelog
  startRecvStream(params) {
    if (this._isTrackExist(params)){
      logger.warn("User media already exists!")
      return
    }
    // 同一个PeerConnection多条流（音视频）应该连接同一个Server
    if (params.streamServer) {
      if (this.streamServer) {
        if (this.streamServer !== params.streamServer) {
          logger.error("New: " + params.streamServer + ", old:" + this.streamServer)
          return
        }
      } else {
        this.streamServer = params.streamServer
      }
    } else {
      logger.error("Invalid stream server!")
      return
    }

    // 请求入队列
    this.startRecvQueue.push(params)

    // 保存Track
    this.downTracks.push({
      userId: params.userId,
      mediaType: params.mediaType,
      mediaId: params.mediaId,
      streamId: params.streamId,
      streamToken: params.streamToken
    })

    // 接收第一条流应该是init状态
    if (this.state == 'init') {
      this.doConnect()
    // 接收第二条流要等待前面的流处理完成再处理
    } else if (this.state == 'connected') {
      this.doLogin()
    } else {
      logger.warn("Do nothing for state: " + this.state)
    }
  }

  /**
   * 停止接收媒体
   * @param {*} params 
   */
  @tracelog
  stopRecvStream(params) {
    if (!this._isTrackExist(params)){
      logger.warn("User media dosen't exist: " + JSON.stringify(params))
      return
    }
    logger.log('current state', this.state)
    if (this.state == 'connected') {
      this.doLogout(params)
    } else { // 请求入队列
      logger.log("Current fsm state: " + this.state + ", cache stop recv item")
      this.stopRecvQueue.push(params)
    }
  }

  /**
   * 关闭
   */
  close () {
    logger.log("Close down peer connection!")

    // 停止统计
    this.stopMediaStats()

    // 主动从管理容器中删除
    this.mediaManager.removeDownPc(this.bindId)

    // 关闭媒体流  不用关闭
    // if (this.mediaStream) {
    //   for (const track of this.mediaStream.getTracks()) {
    //     track.stop()
    //   }
    //   this.mediaStream = null
    // }

    this.downTracks = []

    // 关闭PeerConnection
    if (this.pc){
      this.pc.close()
      this.pc = null
    }

    // 关闭媒体网关的信令连接
    if (this.websocket) {
      this.websocket.close()
      this.websocket = null
    }
  }

  /**
   * 收到服务器发送的Offer处理
   * @param {*} sdp 
   */
  _onRecvSdpOffer(sdp){
    if (this.pc) {
      this.pc.setRemoteDescription(new RTCSessionDescription({ type: "offer", sdp: sdp })).then(() => {
        this._createAnswer()
      }).catch(error => {
        logger.error(error)
        eventRegistry.trigger('onError', {code: 4, msg: 'setRemoteDescription failed: ' + error.message})
      })
    } else {
      logger.warn("Invalid down peer connection!")
    }
  }

  /**
   * 收到服务器发送的Candidate处理
   * @param {*} data 
   */
  _onRecvCandidate(data){
    if (this.pc) {
      var iceCandidate = new RTCIceCandidate({
        sdpMLineIndex: data.sdpMLineIndex,
        candidate: data.candidate
      })
  
      this.pc.addIceCandidate(iceCandidate).then(()=>{
        logger.log("Add candidate success.")
      }).catch((error)=>{
        logger.error("Add candidate failed!" + error)
      })
    } else {
      logger.warn("Invalid down peer connection!")
    }
  }

  /**
   * 判断指定track是否存在
   * @param {*} params 
   */
  _isTrackExist(params){
    for (const track of this.downTracks){
      if (track.userId == params.userId 
        && track.mediaType == params.mediaType 
        && track.mediaId == params.mediaId){
        return true
      }
    }
    return false
  }

  /**
   * 媒体连接状态变化
   * @param {*} event 
   */
  _onConnectionStateChange(event) {
    logger.log("Current peer connection state: " + this.pc.connectionState)

    // 以媒体连接状态为参考
    if (this.pc.connectionState == 'connected' 
      || this.pc.connectionState == 'disconnected'
      || this.pc.connectionState == 'closed') {
      if (this.state == 'negotiating') {
        this.doneNegotiate()
      }
    } else if (this.pc.connectionState === 'failed' && this.state == 'connected') { // 当peerconnection失败  对象状态完成时 重新订阅消息
      this.websocket.close() // 
    }
  }

  /**
   * WebRTC生成Candidate回调
   * @param {*} event 
   */
  _onIceCandidate(event) {
    if (event.candidate) {
      protocolSender.sendCandidate(this.websocket, event, this.pcId)
    } else {
      logger.log("Unexpected candidate: " + event)
    }
  }

  /**
   * 协议连接状态变化
   * @param {*} event 
   */
  _onSignalingstatechange(event) {
    if (this.pc) {
      logger.log("Current signaling state: " + this.pc.signalingState)
      if (this.pc.signalingState == "stable") {
        // this.startRecvQueue.shift()
        logger.log("Current recv queue size: " + this.startRecvQueue.length)

        // 同时接收两条流，远端停止广播一条流，重协商过程中，媒体连接不会重新变为
        // connected，此时需要依赖信令连接的状态变化
        if (this.state == 'negotiating') {
          this.doneNegotiate()
        }
      }
    }
  }

  /**
   * 远端添加媒体流回调
   * @param {*} event 
   */
  _onRemoteTrackAdded(event) {
    this.mediaStream = event.streams[0]
    this.mediaStream.onremovetrack = this._onRemoteTrackRemoved.bind(this)

    this.startAudioStats(this.mediaStream)

    let mediaTrack = null
    for (const track of this.downTracks){
      if ((track.mediaType == 1 && event.track.kind == "audio")
        || (track.mediaType == 2 && event.track.kind == "video")
        || (track.mediaType == 0 && event.track.kind == "video")){ // screen share
        mediaTrack = track
        break
      }
    }

    if (mediaTrack) {
      let params = {
        userId: mediaTrack.userId, 
        mediaType: mediaTrack.mediaType, 
        mediaId: mediaTrack.mediaId,
        bindId: this.bindId
      }
      // 如果是恢复流程则不需要通知上层应用
      // if (!mediaManager.tryRestoreMediaRender(params)) {
      // fix bug 上层音频视频分开渲染时 会导致渲染为同一个节点
        eventRegistry.trigger("onRemoteMediaAdd", params)
      // }

      if (mediaTrack.mediaType == MediaType.AUDIO) {
        this._reportAudioBillStart(mediaTrack.streamId)
      }
    } else {
      logger.log("Cannot find track on remote track add!")
    }
  }

  /**
   * 远端删除媒体流回调
   * @param {*} event 
   */
  _onRemoteTrackRemoved(event) {
    logger.log("onRemoteTrackRemoved, remain track: " + this.mediaStream.getTracks().length)
  }


  /**
   * 构建SDP的Answer
   */
  _createAnswer() {
    let self = this
    self.pc.createAnswer().then(description => {
      self.pc.setLocalDescription(description).then(() => {
        protocolSender.sendSdpInfo(this.websocket, description, this.pcId)
      }).catch(err => {
        eventRegistry.trigger('onError', {code: 4, msg: 'setLocalDescription failed: ' + err.message})
        logger.error(err)
      })
    }).catch(err => {
      logger.error(err)
    })
  }

  /**
   * WebSocket连接成功
   * @param {*} event 
   */
  _onWebsocketOpen(event) {
    logger.log("Down peer connetion websocket created.")
    this.connectSuccess()
  }

  /**
   * WebSocket连接错误
   * @param {*} event 
   */
  _onWebsocketError(event) {
    logger.error("Down peer connection websocket error!")
    this.connectFail()
  }

  /**
   * WebSocket连接关闭
   * @param {*} event 
   */
  _onWebsocketClosed(event) {
    logger.log("Down peer connection websocket closed!")
    // FIXME: 其它状态也需要处理
    if (this.state == 'connected' || this.state == 'connectFailed') {
      for (const track of this.downTracks) {
        asyncWrapper(()=>{this.mediaManager.restoreDownTrack({
          userId: track.userId,
          mediaType: track.mediaType,
          mediaId: track.mediaId,
          streamId: track.streamId,
          streamToken: track.streamToken,
          bindId: this.bindId,
          videoEle: this.videoEle})
        })
      }
      this.doClose()
      // this.retryLogin() // 尝试重新登录
    }
   
    // FIXME：计费处理
    if (this.state != 'closed') {
      this.close()
    }
  }

  /**
   * 登录媒体接收通道响应
   * @param {*} msg 
   */
  _processLoginResp(msg) {
    try {
      if (msg.result == 0) {
        this.loginSuccess(msg)
      } else {
        logger.error("Login webrtc media server failed!")
        this.startRecvQueue.shift()
        this.loginFailed()
      }
    } catch(e) {
      logger.error(JSON.stringify(e))
    }
  }

  /**
   * 登出媒体接收通道响应
   * @param {*} msg 
   */
  _processLogoutResp(msg) {
    try {
      (msg.result == 0) ? this.logoutSuccess(msg.streamId) : this.logoutFailed()
    } catch(e) {
      logger.error(JSON.stringify(e))
    }
  }

  /**
   * 收到PeerConnection协商信令
   * @param {*} msg 
   */
  _processSdpNegotiation(msg) {
    if (msg.type) { // 存在type字段，说明是SDP协商消息
      if (msg.type === "offer") {
        this._onRecvSdpOffer(msg.sdp)
      } else {
        logger.warn("Unknown msg type: " + msg.type)
      }
    } else { // 没有type字段，是Candidate消息
      if (msg.candidate) {
        this._onRecvCandidate(msg)
      } else {
        logger.warn("Unexpected msg without candidate!")
      }
    }
  }

  /**
   * 媒体网关通知视频分辨率变化，需要转发到信令网关做计费
   * @param {*} msg 
   */
  _processResolutionNotify(msg) {
    let params = {
      streamId: msg.stream_id,
      width: msg.width,
      height: msg.height,
      channelType: 1, // recv
      reportType: this.videoBillStarted ? 1/* change */ : 0/* start */
    }

    if (!this.videoBillStarted) {
      this.videoBillStarted = true
    }

    // 对于视频来说，说明流已经通了，收到消息只接上报
    protocolSender.sendResolutionReport(this.websocket, params)
  }

  /**
   * 音频计费开始
   * @param {*} streamId 
   */
  _reportAudioBillStart(streamId) {
    let params = {
      streamId: streamId,
      width: 0,
      height: 0,
      channelType: 1, // recv
      reportType: 1 /* start */
    }
    protocolSender.sendResolutionReport(this.websocket, params)
  }

  /**
   * 计费结束
   * @param {*} streamId 
   */
  _reportBillStop(streamId) {
    let params = {
      streamId: streamId,
      width: 0,
      height: 0,
      channelType: 1, // recv
      reportType: 2 /* stop */
    }
    protocolSender.sendResolutionReport(this.websocket, params)
  }
}

//------------------------------------------------------------------------------
// 状态机
//------------------------------------------------------------------------------

StateMachine.factory(DownPeerConnection, {
  init: 'init',
  transitions: [
    {name: 'doConnect',      from: 'init',                       to: 'connecting'},  // 连接信令网关
    {name: 'connectSuccess', from: 'connecting',                 to: 'connected'},   // 连接信令网关成功
    {name: 'connectFail',  from: 'connecting',                   to: 'connectFailed'}, // 连接信令网关失败
    {name: 'doLogin',        from: 'connected',                  to: 'logining'},    // 登录媒体网关
    {name: 'loginSuccess',   from: 'logining',                   to: 'logined'},     // 登录媒体网关成功
    {name: 'loginFailed',    from: 'logining',                   to: 'connected'},   // 登录媒体网关失败
    {name: 'retryLogin',     from: 'logining',                   to: 'connected'}, // 重试登录媒体网关
    {name: 'doneLogin',      from: 'logined',                    to: 'negotiating'}, // 媒体协商
    {name: 'doneNegotiate',  from: 'negotiating',                to: 'connected'},   // 完成媒体协商
    {name: 'doLogout',       from: 'connected',                  to: 'logouting'},   // 登出媒体网关
    {name: 'logoutSuccess',  from: 'logouting',                  to: 'logouted'},    // 登出媒体网关成功
    {name: 'logoutFailed',   from: 'logouting',                  to: 'connected'},   // 登出媒体网关失败
    {name: 'doneLogout',     from: 'logouted',                   to: 'negotiating'}, // 完成登出
    {name: 'doClose',        from: ['connected', 'logouted', 'connectFailed'],    to: 'closed'},      // 关闭
  ],
  methods: {
    /**
     * 打印状态转移信息
     * @param {*} lifecycle 
     */
    onTransition(lifecycle) {
      logger.state("transition: " + lifecycle.transition + ", from " + lifecycle.from + " to " + lifecycle.to)
    },

    /**
     * connecting状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onConnecting(lifecycle, params) {
      if (!this.streamServer) {
        logger.error("Invalid stream server!");
        asyncWrapper(()=>{this.connectFail()})
      } else {
        this.websocket = new HstWebSocket({
          addrUrl: [this.streamServer],
          handler: this.msgDispatcher,
          opened: this._onWebsocketOpen.bind(this),
          closed: this._onWebsocketClosed.bind(this),
          error: this._onWebsocketError.bind(this)
        })
      }
    },

    /**
     * connected状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onConnected(lifecycle, params) {
      if (this.startRecvQueue.length > 0) {
        asyncWrapper(()=>{this.doLogin()})
        return
      }
      
      if (this.stopRecvQueue.length > 0) {
        logger.log("Stop recv queue length: " + this.stopRecvQueue.length)
        asyncWrapper(()=>{this.doLogout(this.stopRecvQueue.shift())})
        return
      }

      if (lifecycle.from != 'connecting') {
        if (this.downTracks.length == 0) {
          asyncWrapper(()=>{this.doClose()})
        }
      } else {
        logger.error("Empty recv queue!")
      }
    },

    /**
     * logining状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onLogining(lifecycle, params) {
      if (this.startRecvQueue.length > 0) {
        protocolSender.sendLoginRecvChannelReq(
          this.websocket, 
          this.startRecvQueue[0], 
          this.bindId)
      }
    },

    /**
     * logined状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onLogined(lifecycle, params) {
      if (!this.pc) {
        try {
          let configure = {
            // enableDtlsSrtp: 0,
            iceServers: [{
              urls: 'stun:fsp-turn1.hst.com:5469'
            }]
            // SdpSemantics: "UnifiedPlan"
          }
          this.pc = new RTCPeerConnection(configure)
          this.pc.onicecandidate          = this._onIceCandidate.bind(this)
          this.pc.onsignalingstatechange  = this._onSignalingstatechange.bind(this)
          this.pc.onconnectionstatechange = this._onConnectionStateChange.bind(this)
        } catch (e) {
          logger.log("Failed to create PeerConnection, exception: " + e.message)
          eventRegistry.trigger('onError', {code: 3, msg: 'create failed: ' + e.message})
          return
        }
        this.pc.ontrack = this._onRemoteTrackAdded.bind(this)
        this.startPcStats(this.pc, true)
      }

      if (!this.pcId) {
        this.pcId = params.pc_id
      } else {
        if (this.pcId != params.pc_id) {
          logger.error("New pc id: " + params.pc_id + ", old pc id: " + this.pcId)
        }
      }

      // 无条件跳转到negotiating状态，等待服务器发送Offer进行协商
      asyncWrapper(()=>{this.doneLogin()})
    },
   /**
     * retryLogin 重试登陆网关处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onRetryLogin() {
      if (this.startRecvQueue.length > 0) {
        asyncWrapper(()=>{this.doLogin()})
        return
      } 
    },
    /**
     * negotiating状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onNegotiating(lifecycle, params) {
      if (lifecycle.from == 'logined') {
        this.startRecvQueue.shift()
      }
      // 从logouted状态跳转过来，track已经清空，不用再等待媒体协商
      if (lifecycle.from == 'logouted'
        && this.mediaStream.getTracks().length == 0 
        && this.downTracks.length == 0) {
        asyncWrapper(()=>{this.doneNegotiate()})
      } else {
        // 队列里面等着处理停止接收，不用等PeerConnection协商了
        // 这里偷了个懒，没有校验Track和停止接收任务是不是同一个媒体流
        if (this.downTracks.length == 1 
          && this.stopRecvQueue.length == 1) {
          asyncWrapper(()=>{this.doneNegotiate()})
        }
      }
    },

    /**
     * logouting状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onLogouting(lifecycle, params) {
      let streamId = null
      this.downTracks.forEach(function(track, index, arr){
        if (track.userId == params.userId
          && track.mediaType == params.mediaType 
          && track.mediaId == params.mediaId){
          arr.splice(index, 1) // 删除指定Track
          streamId = track.streamId
          return false // 跳出forEach循环
        }
      })

      // 不管Tack在不在，都需要完成Logout
      protocolSender.sendLogoutRecvChannelReq(this.websocket, streamId)

      if (!streamId) {
        logger.warn("Remove track failed: " + JSON.stringify(params))
        // asyncWrapper(()=>{this.logoutFailed()})
      }
      // fix 修复反复接收，上一次连接还未完全关闭，下一次连接建立被阻止的问题
      if (this.downTracks.length == 0) {
        this._reportBillStop(streamId)
        this.close()
      }
    },

    /**
     * logouted状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onLogouted(lifecycle, params) {
      this._reportBillStop(params)
      if (this.downTracks.length == 0) {
        logger.log("None down tracks, then close peer connection!")
        asyncWrapper(()=>{this.doClose()})
      } else {
        asyncWrapper(()=>{this.doneLogout()})
      }
    },

    /**
     * closed状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onClosed(lifecycle, params) {
      this.close()
    },
  }
})

export default DownPeerConnection